import { motion } from 'framer-motion'
import { Mail, Phone } from 'lucide-react'
import type { Coach } from '@/types/api'

export function CoachesGrid({ coaches }: { coaches: Coach[] }) {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {coaches.map((c, i) => (
        <motion.article
          key={c.slug}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10% 0px' }}
          transition={{ delay: i * 0.08, duration: 0.5 }}
          className="rounded-2xl overflow-hidden border border-border bg-card hover:shadow-lg hover:-translate-y-1 transition-all"
        >
          <div className="aspect-[4/5] relative overflow-hidden bg-pitch-800">
            <img
              src={c.image}
              alt={c.name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover object-top"
              onError={(e) => {
                const img = e.currentTarget
                img.style.display = 'none'
                img.parentElement?.classList.add('bg-gradient-to-br', 'from-pitch-800', 'to-pitch-950')
              }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
          </div>
          <div className="p-5">
            <div className="font-display text-xl uppercase tracking-tight">{c.name}</div>
            <div className="mt-1 text-[11px] uppercase tracking-widest text-muted-foreground">{c.role}</div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {c.licenses.map((l) => (
                <span key={l} className="bg-muted text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-2 py-1 rounded">
                  {l}
                </span>
              ))}
            </div>
            {(c.email || c.phone) && (
              <div className="mt-4 pt-3 border-t border-border space-y-1.5 text-xs text-muted-foreground">
                {c.email && (
                  <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:text-cosmos-green">
                    <Mail className="h-3 w-3" /> {c.email}
                  </a>
                )}
                {c.phone && (
                  <a href={`tel:${c.phone}`} className="flex items-center gap-2 hover:text-cosmos-green">
                    <Phone className="h-3 w-3" /> {c.phone}
                  </a>
                )}
              </div>
            )}
          </div>
        </motion.article>
      ))}
    </div>
  )
}
